var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-tabs.ts
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { fromEvent, takeUntil } from "rxjs";
import { ChemistryLitElement } from "../../chemistry-lit-element.js";
import { RXController } from "../../controllers/RXController.js";
import { ComponentSizeController } from "../../controllers/component-size-controller.js";
import { event } from "../../decorators/event.js";
import { css } from "../../directives/css.js";
import { DesignSystem } from "../theming/design.js";
import { xComponents } from "../x-components.js";
import "./x-card.js";
import "./x-flex.js";
import "./x-icon.js";
import "./x-link.js";
var XTabs = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this._size = new ComponentSizeController(this, { debounceTime: 200 });
    this.selected = new RXController(this, -1);
    this.selectedColor = "";
    this.customClick = false;
    this._marker = { startPosition: 0, width: 0 };
    this._eventSubs = [];
  }
  styles() {
    return [
      {
        ":host": {
          position: "relative",
          overflowX: "auto",
          maxWidth: "100%",
          borderBottom: `1px solid ${this.colors.neutral.shade(100)}`
        }
      }
    ];
  }
  firstUpdated(chp) {
    super.firstUpdated(chp);
    this._initLabels();
  }
  updated(chp) {
    super.updated(chp);
    const selected = this.selected.value;
    if (typeof selected === "undefined")
      return;
    const labels = this._slot?.assignedElements() ?? [];
    const selectedLabel = this._getSelectedLabel(labels, selected);
    if (selectedLabel) {
      const newStartPosition = selectedLabel?.offsetLeft - 1 || 0;
      const newWidth = selectedLabel?.offsetWidth ?? 0;
      if (this._marker.startPosition !== newStartPosition || this._marker.width !== newWidth) {
        this._marker = {
          startPosition: newStartPosition,
          width: newWidth
        };
      }
    } else if (this._marker.startPosition !== 0 || this._marker.width !== 0)
      this._marker = { startPosition: 0, width: 0 };
    labels.filter((label) => label !== selectedLabel).forEach((label) => label.removeAttribute("selected"));
    selectedLabel?.setAttribute("selected", "");
  }
  _getSelectedLabel(labels, selected) {
    for (const label of labels) {
      if (label.label === selected)
        return label;
    }
  }
  _initLabels() {
    for (const sub of this._eventSubs)
      sub.unsubscribe();
    this._eventSubs = [];
    const labels = this._slot.assignedElements();
    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (typeof label.label === "undefined")
        label.label = i;
      label.selectedColor = this.selectedColor;
      if (!this.customClick) {
        this._eventSubs.push(
          fromEvent(label, "click").pipe(takeUntil(this.disconnected)).subscribe(() => this.selected.source.next(label.label))
        );
      }
    }
  }
  _slotChange() {
    this._initLabels();
  }
  render() {
    return xComponents(html`
      <x-flex horizontal ${css({ position: "relative" })}>
        <slot
          id="labels"
          ${ref((slot) => this._slot = slot)}
          @slotchange=${() => this._slotChange()}
        ></slot>
        ${this.markerElement()}
      </x-flex>
    `);
  }
  markerElement() {
    return html`
      <div
        ${css({
      height: "2px",
      background: this.selectedColor ? this.selectedColor : this.colors.accent,
      width: "100px",
      position: "absolute",
      bottom: "0",
      left: "0",
      transformOrigin: "left",
      transform: `translateX(${this._marker.startPosition}px) scaleX(${this._marker.width / 100})`,
      transition: "transform .2s"
    })}
      ></div>
    `;
  }
};
__decorateClass([
  state()
], XTabs.prototype, "_size", 2);
__decorateClass([
  event,
  property()
], XTabs.prototype, "selected", 2);
__decorateClass([
  property()
], XTabs.prototype, "selectedColor", 2);
__decorateClass([
  property({ attribute: "custom-click", type: Boolean })
], XTabs.prototype, "customClick", 2);
__decorateClass([
  state()
], XTabs.prototype, "_marker", 2);
XTabs = __decorateClass([
  customElement("x-tabs")
], XTabs);
var XTab = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.selectedColor = "";
    this.icon = "";
    this.inferredText = "";
    this.renderContent = (el) => html` <x-h4 ellipsis .value=${el.inferredText}></x-h4> `;
  }
  styles() {
    return [
      {
        ":host": {
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          flex: "1",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          borderBottom: "2px solid transparent",
          userSelect: "none"
        },
        ":host([selected])": { color: this.selectedColor ? this.selectedColor : this.colors.accent },
        ":host([secondary])": { fontWeight: "lighter", fontSize: "12px" },
        ":host([big])": { height: "70px" },
        // '@media (max-width: 992px)': { height: '50px' },
        [`${DesignSystem.prefix}-h4`]: { width: "unset" },
        "@media screen and (max-width: 500px)": {
          [`${DesignSystem.prefix}-h4`]: { fontSize: "14px" }
        }
      }
    ];
  }
  firstUpdated(changedProps) {
    super.firstUpdated(changedProps);
    this._updateContents();
  }
  render() {
    const content = html`
      <x-flex crossaxis-center center horizontal ${css({ height: "100%", width: "100%", padding: this.spacing.md })}>
        ${this.icon ? html` <x-icon ${css({ marginRight: this.spacing.sm })}> ${this.icon} </x-icon> ` : nothing}
        ${this.renderContent(this)} ${this.slotGrabber()}
      </x-flex>
    `;
    return xComponents(
      this.href ? html` <x-link ${css({ width: "100%", height: "100%" })} href=${this.href}> ${content} </x-link> ` : content
    );
  }
  slotGrabber() {
    return html`
      <div ${css({ display: "none" })}>
        <slot
          ${ref((slot) => this._slot = slot)}
          @slotchange=${(e) => {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = void 0;
      }
      this._observer = new MutationObserver((mutationsList, observer) => this._updateContents());
      this._slot?.assignedNodes({ flatten: true }).forEach((node) => {
        this._observer.observe(node, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      });
      this._updateContents();
    }}
        ></slot>
      </div>
    `;
  }
  _updateContents() {
    const childNodes = this._slot?.assignedNodes({ flatten: true }) ?? [];
    this.inferredText = childNodes.map((node) => node.textContent).join(" ");
  }
};
__decorateClass([
  state()
], XTab.prototype, "selectedColor", 2);
__decorateClass([
  property()
], XTab.prototype, "label", 2);
__decorateClass([
  property()
], XTab.prototype, "icon", 2);
__decorateClass([
  property()
], XTab.prototype, "href", 2);
__decorateClass([
  property()
], XTab.prototype, "inferredText", 2);
XTab = __decorateClass([
  customElement("x-tab")
], XTab);
export {
  XTab,
  XTabs
};
